import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, formatDate, fileUrl } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, CreditCard, TrendingUp, TrendingDown, Scale, ShieldCheck, HardHat, Paperclip, FileText, X, Loader2, Minus, Banknote, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";

const INCOME_CATS = ["rent", "deposit", "deposit_refund", "electricity", "water", "broken_item", "laundry", "misc"];
const EXPENSE_CATS = ["builder", "pool", "repairs", "furniture", "taxes", "internet", "electricity", "water", "misc"];

const emptyPay = { reservation_id: "", tenant_id: "", unit_id: "", direction: "income", category: "rent", amount: "", method: "cash", status: "paid", period_label: "", note: "", date: "", receipt_path: "", receipt_name: "", receipt_type: "", send_receipt: true };
// Set to true to re-enable Stripe online payments (requires activating the Stripe account / KYC).
const SHOW_ONLINE_PAYMENT = false;

// Legacy fallback for rows saved before the income/expense model.
const rowDirection = (p) => p.direction || (p.kind === "expense" ? "expense" : "income");
const rowCategory = (p) => p.category || (p.kind === "deposit_refund" ? "deposit_refund" : ["rent", "deposit"].includes(p.kind) ? p.kind : "misc");
// A guarantee (deposit) or its refund is held money, not our revenue/expense.
const isDeposit = (p) => ["deposit", "deposit_refund"].includes(rowCategory(p));
const isMoneyOut = (p) => rowDirection(p) === "expense" || rowCategory(p) === "deposit_refund";

export default function Finances() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data: payments = [] } = useQuery({ queryKey: ["payments"], queryFn: () => api.get("/payments").then((r) => r.data) });
  const { data: reservations = [] } = useQuery({ queryKey: ["reservations"], queryFn: () => api.get("/reservations").then((r) => r.data) });
  const { data: tenants = [] } = useQuery({ queryKey: ["tenants"], queryFn: () => api.get("/tenants").then((r) => r.data) });
  const { data: units = [] } = useQuery({ queryKey: ["units"], queryFn: () => api.get("/units").then((r) => r.data) });
  const { data: dues = [] } = useQuery({ queryKey: ["dues"], queryFn: () => api.get("/dues").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const [online, setOnline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const tName = (id) => { const tn = tenants.find((x) => x.id === id); return tn ? `${tn.first_name} ${tn.last_name}` : "—"; };
  const uName = (id) => { const u = units.find((x) => x.id === id); return u ? u.number : "—"; };
  const resLabel = (r) => `${uName(r.unit_id)} · ${tName(r.tenant_id)}`;
  const catLabel = (c) => t(`fin.cat_${c}`);

  const applyRes = (rid, setter, cur) => {
    const r = reservations.find((x) => x.id === rid);
    setter({ ...cur, reservation_id: rid, tenant_id: r?.tenant_id || "", unit_id: r?.unit_id || "",
      amount: cur.amount || (cur.category === "deposit" ? r?.deposit_amount : r?.rent_amount) || "" });
  };

  const setDirection = (dir) => setForm({ ...form, direction: dir, category: dir === "income" ? "rent" : "pool" });

  const uploadReceipt = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/payments/receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_path: data.receipt_path, receipt_name: data.receipt_name, receipt_type: data.receipt_type }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload error");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.amount) { toast.error(t("fin.amount")); return; }
    const payload = { ...form, amount: Number(form.amount) };
    const { data } = form.id
      ? await api.put(`/payments/${form.id}`, payload)
      : await api.post("/payments", payload);
    qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["dues"] });
    setForm(null);
    toast.success(!form.id && data?.receipt_sent ? t("fin.receiptSent") : t("common.save"));
  };

  const openEdit = (p) => setForm({
    id: p.id, reservation_id: p.reservation_id || "", tenant_id: p.tenant_id || "", unit_id: p.unit_id || "",
    direction: rowDirection(p), category: rowCategory(p), amount: p.amount, method: p.method || "cash",
    status: p.status || "paid", period_label: p.period_label || "", note: p.note || "", date: p.date || "",
    receipt_path: p.receipt_path || "", receipt_name: p.receipt_name || "", receipt_type: p.receipt_type || "", send_receipt: false,
  });

  const collectRent = async (row) => {
    const next = row.months.find((m) => m.status !== "paid");
    if (!next) return;
    const [y, m] = next.period.split("-");
    const { data } = await api.post("/payments", {
      reservation_id: row.reservation_id, tenant_id: row.tenant_id, unit_id: row.unit_id,
      direction: "income", category: "rent", amount: row.rent_amount, method: "cash", status: "paid",
      period_label: next.period, note: `${t("dues.rent")} ${next.period}`, date: `${y}-${m}-01T00:00:00+00:00`,
    });
    qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["stats"] }); qc.invalidateQueries({ queryKey: ["dues"] });
    toast.success(data?.receipt_sent ? t("fin.receiptSent") : t("dues.markPaid"));
  };

  const remove = async (id) => {
    if (!window.confirm(t("fin.deleteConfirm"))) return;
    await api.delete(`/payments/${id}`);
    qc.invalidateQueries({ queryKey: ["payments"] }); qc.invalidateQueries({ queryKey: ["stats"] });
    toast.success(t("common.delete"));
  };

  const payOnline = async () => {
    if (!online.amount) { toast.error(t("fin.amount")); return; }
    setLoading(true);
    try {
      const label = `${catLabel(online.category)} · ${online.unit_id ? uName(online.unit_id) : "NG Residence"}`;
      const { data } = await api.post("/payments/checkout", {
        reservation_id: online.reservation_id, tenant_id: online.tenant_id, unit_id: online.unit_id,
        kind: online.category, amount: Number(online.amount), label, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch { toast.error("Stripe error"); setLoading(false); }
  };

  // Monthly synthesis (Finances page only). Deposits/refunds are held money, excluded from revenue & expenses.
  const ym = new Date().toISOString().slice(0, 7);
  const monthly = payments.filter((p) => (p.date || "").startsWith(ym) && p.status === "paid");
  const incomeMonth = monthly.filter((p) => rowDirection(p) === "income" && !isDeposit(p)).reduce((s, p) => s + p.amount, 0);
  const expenseMonth = monthly.filter((p) => rowDirection(p) === "expense" && !isDeposit(p)).reduce((s, p) => s + p.amount, 0);
  const netMonth = incomeMonth - expenseMonth;
  const depositsHeld = payments.filter((p) => p.status === "paid" && rowCategory(p) === "deposit").reduce((s, p) => s + p.amount, 0)
    - payments.filter((p) => rowCategory(p) === "deposit_refund").reduce((s, p) => s + p.amount, 0);
  const builderTotal = payments.filter((p) => p.status === "paid" && rowCategory(p) === "builder").reduce((s, p) => s + p.amount, 0);

  const statCards = [
    { key: "inc", label: t("fin.incomeMonth"), value: money(incomeMonth), icon: TrendingUp, cls: "text-[#2D6A4F]" },
    { key: "exp", label: t("fin.expenseMonth"), value: money(expenseMonth), icon: TrendingDown, cls: "text-destructive" },
    { key: "net", label: t("fin.netMonth"), value: money(netMonth), icon: Scale, cls: netMonth >= 0 ? "text-teak" : "text-destructive" },
    { key: "dep", label: t("fin.depositsHeld"), value: money(depositsHeld), icon: ShieldCheck, cls: "text-terracotta" },
    { key: "builder", label: t("fin.builderTotal"), value: money(builderTotal), icon: HardHat, cls: "text-teak" },
  ];

  const cats = form?.direction === "expense" ? EXPENSE_CATS : INCOME_CATS;
  const alertRows = dues.filter((r) => r.overdue > 0 || r.due_now > 0);
  const tenantEmail = form ? (tenants.find((x) => x.id === form.tenant_id)?.email || "") : "";
  // Cumulative expense breakdown by category and payment method (all-time, not month-limited).
  const expenseRows = (() => {
    const byCat = {};
    payments.filter((p) => rowDirection(p) === "expense" && p.status === "paid").forEach((p) => {
      const c = rowCategory(p);
      byCat[c] = byCat[c] || { cash: 0, transfer: 0, card: 0, total: 0 };
      const m = ["cash", "transfer", "card"].includes(p.method) ? p.method : "card";
      byCat[c][m] += p.amount; byCat[c].total += p.amount;
    });
    return Object.entries(byCat).map(([cat, v]) => ({ cat, ...v })).sort((a, b) => b.total - a.total);
  })();
  const expenseGrand = expenseRows.reduce((s, r) => ({ cash: s.cash + r.cash, transfer: s.transfer + r.transfer, card: s.card + r.card, total: s.total + r.total }), { cash: 0, transfer: 0, card: 0, total: 0 });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("fin.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("fin.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {SHOW_ONLINE_PAYMENT && <Button variant="outline" onClick={() => setOnline({ ...emptyPay })} data-testid="pay-online-btn"><CreditCard className="w-4 h-4 mr-1" />{t("fin.payOnline")}</Button>}
          <Button variant="outline" onClick={() => setForm({ ...emptyPay, direction: "expense", category: "pool" })} className="border-destructive/40 text-destructive hover:bg-[#F9EAEB]" data-testid="add-expense-btn"><TrendingDown className="w-4 h-4 mr-1" />{t("fin.addExpense")}</Button>
          <Button onClick={() => setForm({ ...emptyPay })} className="bg-teak hover:bg-teak-hover text-white" data-testid="add-payment-btn"><Plus className="w-4 h-4 mr-1" />{t("fin.addIncome")}</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map((c) => (
          <Card key={c.key} className="p-4 border-border" data-testid={`stat-${c.key}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{c.label}</span><c.icon className={`w-4 h-4 ${c.cls}`} /></div>
            <div className={`text-2xl font-semibold mt-2 ${c.cls}`}>{c.value}</div>
          </Card>
        ))}
      </div>

      <FxConverter />

      {expenseRows.length > 0 && (
        <Card className="border-border overflow-hidden" data-testid="expense-breakdown">
          <div className="px-4 pt-4 pb-2 text-xs uppercase tracking-[0.08em] text-muted-foreground">{t("fin.expenseByCategory")}</div>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>{t("fin.category")}</TableHead>
              <TableHead className="text-right">{t("fin.cash")}</TableHead>
              <TableHead className="text-right">{t("fin.transfer")}</TableHead>
              <TableHead className="text-right">{t("fin.card")}</TableHead>
              <TableHead className="text-right">{t("fin.total")}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {expenseRows.map((r) => (
                <TableRow key={r.cat} data-testid={`exp-cat-${r.cat}`}>
                  <TableCell><Badge className="border-0 bg-[#F9EAEB] text-destructive">{catLabel(r.cat)}</Badge></TableCell>
                  <TableCell className="text-right text-sm">{r.cash ? money(r.cash) : "—"}</TableCell>
                  <TableCell className="text-right text-sm">{r.transfer ? money(r.transfer) : "—"}</TableCell>
                  <TableCell className="text-right text-sm">{r.card ? money(r.card) : "—"}</TableCell>
                  <TableCell className="text-right font-semibold text-destructive">{money(r.total)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-secondary/40 font-semibold" data-testid="exp-cat-total">
                <TableCell>{t("fin.total")}</TableCell>
                <TableCell className="text-right text-sm">{money(expenseGrand.cash)}</TableCell>
                <TableCell className="text-right text-sm">{money(expenseGrand.transfer)}</TableCell>
                <TableCell className="text-right text-sm">{money(expenseGrand.card)}</TableCell>
                <TableCell className="text-right text-destructive">{money(expenseGrand.total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      )}

      {alertRows.length > 0 && (
        <Card className="border-[#f0dcc4] bg-gold-light/40 p-4" data-testid="rent-alert">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-terracotta" />
            <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-terracotta">{t("fin.rentAlert")}</h2>
          </div>
          <div className="space-y-2">
            {alertRows.map((r) => (
              <div key={r.reservation_id} className="flex flex-wrap items-center gap-3 rounded-md bg-white/70 border border-border px-3 py-2" data-testid={`rent-alert-row-${r.reservation_id}`}>
                <span className="font-semibold w-14">{r.unit_number}</span>
                <span className="text-sm text-muted-foreground flex-1 min-w-[120px] truncate">{r.tenant_name}</span>
                {r.overdue > 0 && <Badge className="bg-[#F9EAEB] text-destructive border-0">{r.overdue} {t("dues.overdue")}</Badge>}
                {r.due_now > 0 && <Badge className="bg-gold-light text-terracotta border-0">{t("dues.dueNow")}</Badge>}
                <span className="font-semibold text-teak">{money(r.outstanding)}</span>
                <Button size="sm" onClick={() => collectRent(r)} className="bg-teak hover:bg-teak-hover text-white" data-testid={`collect-rent-${r.reservation_id}`}>
                  <Banknote className="w-4 h-4 mr-1" />{t("fin.collect")}
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>{t("fin.date")}</TableHead>
              <TableHead>{t("fin.category")}</TableHead>
              <TableHead>{t("res.unit")}</TableHead>
              <TableHead>{t("res.tenant")}</TableHead>
              <TableHead>{t("fin.period")}</TableHead>
              <TableHead>{t("fin.method")}</TableHead>
              <TableHead className="text-right">{t("fin.amount")}</TableHead>
              <TableHead>{t("fin.status")}</TableHead>
              <TableHead>{t("fin.receipt")}</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">{t("fin.empty")}</TableCell></TableRow>
            ) : payments.map((p) => {
              const out = isMoneyOut(p);
              const dep = isDeposit(p);
              const badgeCls = dep ? "bg-gold-light text-terracotta" : out ? "bg-[#F9EAEB] text-destructive" : "bg-[#EAF4F0] text-[#2D6A4F]";
              const amtCls = dep ? "text-terracotta" : out ? "text-destructive" : "text-[#2D6A4F]";
              return (
              <TableRow key={p.id} className="hover:bg-[#FDFDFB]" data-testid={`pay-row-${p.id}`}>
                <TableCell className="text-sm text-muted-foreground">{formatDate(p.date)}</TableCell>
                <TableCell><Badge className={`border-0 ${badgeCls}`}>{catLabel(rowCategory(p))}</Badge></TableCell>
                <TableCell className="font-semibold">{p.unit_id ? uName(p.unit_id) : "—"}</TableCell>
                <TableCell>{p.tenant_id ? tName(p.tenant_id) : "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.period_label || p.note || "—"}</TableCell>
                <TableCell className="text-sm">{t(`fin.${p.method === "stripe" ? "online" : p.method}`)}</TableCell>
                <TableCell className={`text-right font-semibold ${amtCls}`}>{out ? "-" : "+"}{money(p.amount)}</TableCell>
                <TableCell><Badge variant={p.status === "paid" ? "default" : "secondary"} className={p.status === "paid" ? "bg-[#2D6A4F]" : ""}>{t(`fin.${p.status}`)}</Badge></TableCell>
                <TableCell>
                  {p.receipt_path ? (
                    <a href={fileUrl(p.receipt_path)} target="_blank" rel="noreferrer" className="inline-flex items-center text-teak hover:underline" title={t("fin.viewReceipt")} data-testid={`pay-receipt-${p.id}`}><FileText className="w-4 h-4" /></a>
                  ) : <Minus className="w-4 h-4 text-muted-foreground/40" />}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`pay-edit-${p.id}`}><Pencil className="w-4 h-4 text-teak" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)} data-testid={`pay-delete-${p.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ); })}
          </TableBody>
        </Table>
      </Card>

      {/* Manual entry dialog (income or expense) */}
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form?.id ? t("fin.editEntry") : (form?.direction === "expense" ? t("fin.addExpense") : t("fin.addIncome"))}</DialogTitle>
            <DialogDescription>{t("fin.subtitle")}</DialogDescription></DialogHeader>
          {form && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>{t("fin.direction")}</Label>
                <Select value={form.direction} onValueChange={setDirection}>
                  <SelectTrigger data-testid="pay-direction-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="income">{t("fin.income")}</SelectItem><SelectItem value="expense">{t("fin.expense")}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("fin.category")}</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="pay-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              {form.direction === "income" && (
                <div className="space-y-2 col-span-2"><Label>{t("res.title")}</Label>
                  <Select value={form.reservation_id} onValueChange={(v) => applyRes(v, setForm, form)}>
                    <SelectTrigger data-testid="pay-res-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                    <SelectContent>{reservations.map((r) => <SelectItem key={r.id} value={r.id}>{resLabel(r)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2 col-span-2"><Label>{t("res.unit")}</Label>
                <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="pay-unit-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.number}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2"><Label>{t("fin.amount")}</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" /></div>
              <div className="space-y-2"><Label>{t("fin.method")}</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">{t("fin.cash")}</SelectItem><SelectItem value="transfer">{t("fin.transfer")}</SelectItem><SelectItem value="card">{t("fin.card")}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("fin.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="pay-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="paid">{t("fin.paid")}</SelectItem><SelectItem value="pending">{t("fin.pending")}</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{t("fin.documentDate")}</Label>
                <Input type="date" value={form.date ? form.date.slice(0, 10) : ""} onChange={(e) => setForm({ ...form, date: e.target.value ? `${e.target.value}T12:00:00+00:00` : "" })} data-testid="pay-date" /></div>
              <div className="space-y-2"><Label>{t("fin.period")}</Label><Input value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} data-testid="pay-period" /></div>
              <div className="space-y-2 col-span-2"><Label>{t("fin.note")}</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="pay-note" /></div>

              <div className="space-y-2 col-span-2">
                <Label>{t("fin.receipt")}</Label>
                {form.receipt_path ? (
                  <div className="flex items-center gap-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-sm">
                    <FileText className="w-4 h-4 text-teak" />
                    <a href={fileUrl(form.receipt_path)} target="_blank" rel="noreferrer" className="flex-1 truncate text-teak hover:underline" data-testid="pay-receipt-link">{form.receipt_name || t("fin.viewReceipt")}</a>
                    <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => setForm({ ...form, receipt_path: "", receipt_name: "", receipt_type: "" })} data-testid="pay-receipt-remove"><X className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground cursor-pointer hover:border-teak hover:text-teak transition-colors" data-testid="pay-receipt-label">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                    <span>{uploading ? t("fin.uploading") : t("fin.addReceipt")}</span>
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => uploadReceipt(e.target.files?.[0])} data-testid="pay-receipt-input" />
                  </label>
                )}
              </div>

              {form.direction === "income" && form.category === "rent" && (
                <div className="col-span-2">
                  {tenantEmail ? (
                    <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="pay-send-receipt-label">
                      <Checkbox checked={form.send_receipt} onCheckedChange={(v) => setForm({ ...form, send_receipt: !!v })} data-testid="pay-send-receipt" />
                      <Mail className="w-4 h-4 text-teak" /> {t("fin.sendReceipt")}
                    </label>
                  ) : (
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail className="w-3 h-3" />{t("fin.noTenantEmail")}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>{t("common.cancel")}</Button>
            <Button onClick={save} disabled={uploading} className="bg-teak hover:bg-teak-hover text-white" data-testid="pay-save-btn">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Online payment dialog */}
      <Dialog open={!!online} onOpenChange={(o) => !o && setOnline(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-teak" />{t("fin.payOnline")}</DialogTitle>
            <DialogDescription>{t("fin.online_desc")}</DialogDescription></DialogHeader>
          {online && (
            <div className="space-y-4">
              <div className="space-y-2"><Label>{t("res.title")}</Label>
                <Select value={online.reservation_id} onValueChange={(v) => applyRes(v, setOnline, online)}>
                  <SelectTrigger data-testid="online-res-select"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>{reservations.map((r) => <SelectItem key={r.id} value={r.id}>{resLabel(r)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>{t("fin.category")}</Label>
                  <Select value={online.category} onValueChange={(v) => setOnline({ ...online, category: v })}>
                    <SelectTrigger data-testid="online-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="rent">{t("fin.rent")}</SelectItem><SelectItem value="deposit">{t("fin.deposit")}</SelectItem><SelectItem value="misc">{t("fin.cat_misc")}</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>{t("fin.amount")}</Label><Input type="number" value={online.amount} onChange={(e) => setOnline({ ...online, amount: e.target.value })} data-testid="online-amount" /></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOnline(null)}>{t("common.cancel")}</Button>
            <Button onClick={payOnline} disabled={loading} className="bg-teak hover:bg-teak-hover text-white" data-testid="online-pay-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Banknote className="w-4 h-4 mr-1" />}{t("fin.payNow")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function FxConverter() {
  const { t } = useI18n();
  const { data } = useQuery({ queryKey: ["fx"], queryFn: () => api.get("/fx").then((r) => r.data), staleTime: 3600000, retry: 1 });
  const rate = data?.chf_thb;
  const [chf, setChf] = useState("");
  const [thb, setThb] = useState("");
  const onChf = (v) => { setChf(v); setThb(v && rate ? (parseFloat(v) * rate).toFixed(0) : ""); };
  const onThb = (v) => { setThb(v); setChf(v && rate ? (parseFloat(v) / rate).toFixed(2) : ""); };
  return (
    <Card className="border-border p-4" data-testid="fx-converter">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{t("fin.converter")}</span>
        <div className="flex items-center gap-2">
          <Input type="number" value={chf} onChange={(e) => onChf(e.target.value)} placeholder="CHF" className="w-28 h-9" data-testid="fx-chf" />
          <span className="text-sm font-medium">CHF</span>
          <span className="text-muted-foreground">⇄</span>
          <Input type="number" value={thb} onChange={(e) => onThb(e.target.value)} placeholder="THB" className="w-32 h-9" data-testid="fx-thb" />
          <span className="text-sm font-medium">฿</span>
        </div>
        <span className="text-xs text-muted-foreground ml-auto" data-testid="fx-rate">{rate ? `1 CHF = ${Number(rate).toFixed(2)} ฿` : "…"}</span>
      </div>
    </Card>
  );
}
